
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const QUALIFIED_ZIPS = ['21001','21005','21009','21010','21014','21015','21017','21028','21034','21040','21047','21050','21051','21078','21082','21084','21085','21087','21111','21120','21128','21131','21132','21154','21156','21160','21161'];

export default function Footer() {
  const navigate = useNavigate();
  const [showBookingChoice, setShowBookingChoice] = useState(false);
  const [bookingStep, setBookingStep] = useState<'choice' | 'zip' | 'result'>('choice');
  const [zipCode, setZipCode] = useState('');
  const [zipValid, setZipValid] = useState<boolean | null>(null);

  const handleLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookEvalClick = () => {
    setShowBookingChoice(true);
    setBookingStep('choice');
    setZipCode('');
    setZipValid(null);
  };

  const handleInPerson = () => {
    setBookingStep('zip');
  };

  const handleZipSubmit = () => {
    const trimmed = zipCode.trim();
    if (trimmed.length !== 5) return;
    if (QUALIFIED_ZIPS.includes(trimmed)) {
      setZipValid(true);
    } else {
      setZipValid(false);
    }
    setBookingStep('result');
  };

  const handleGoToBooking = () => {
    setShowBookingChoice(false);
    navigate('/qualified-booking');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const closeModal = () => {
    setShowBookingChoice(false);
    setBookingStep('choice');
    setZipCode('');
    setZipValid(null);
  };

  return (
    <>
      <footer className="bg-[#1a2e0a] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {/* Company Info */}
            <div>
              <img 
                src="https://jslandscapingmd.com/wp-content/uploads/2023/07/Js-logo-clear.png" 
                alt="J's Landscaping Services LLC" 
                className="h-10 sm:h-12 w-auto mb-4"
              />
              <p className="text-gray-300 mb-4 text-sm sm:text-base">
                Professional landscaping and complete property management services in Maryland.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-[#83B328] transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="text-gray-400 hover:text-[#83B328] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><Link to="/" onClick={handleLinkClick} className="text-gray-300 hover:text-[#83B328] transition-colors text-sm sm:text-base cursor-pointer">Home</Link></li>
                <li><Link to="/services" onClick={handleLinkClick} className="text-gray-300 hover:text-[#83B328] transition-colors text-sm sm:text-base cursor-pointer">Services</Link></li>
                <li><Link to="/case-studies" onClick={handleLinkClick} className="text-gray-300 hover:text-[#83B328] transition-colors text-sm sm:text-base cursor-pointer">Case Studies</Link></li>
                <li>
                  <button 
                    onClick={handleBookEvalClick} 
                    className="text-gray-300 hover:text-[#83B328] transition-colors text-sm sm:text-base cursor-pointer text-left"
                  >
                    Book an Evaluation
                  </button>
                </li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Contact</h3>
              <div className="space-y-2 sm:space-y-3">
                <div className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 mt-0.5">
                    <i className="ri-phone-line text-[#83B328] text-sm sm:text-base"></i>
                  </div>
                  <a href="#" className="call-trigger text-gray-300 hover:text-[#83B328] transition-colors text-sm sm:text-base cursor-pointer" data-phone="4438191521">
                    (443) 819-1521
                  </a>
                </div>
                <div className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 mt-0.5">
                    <i className="ri-mail-line text-[#83B328] text-sm sm:text-base"></i>
                  </div>
                  <a href="mailto:Jordan@JslandscapingMD.com" className="text-gray-300 hover:text-[#83B328] transition-colors text-sm sm:text-base break-all cursor-pointer">
                    Jordan@JslandscapingMD.com
                  </a>
                </div>
                <div className="flex items-start">
                  <div className="w-5 h-5 flex items-center justify-center mr-2 mt-0.5">
                    <i className="ri-map-pin-line text-[#83B328] text-sm sm:text-base"></i>
                  </div>
                  <span className="text-gray-300 text-sm sm:text-base">
                    Serving Maryland
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 mt-6 sm:mt-8 pt-6 sm:pt-8">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              <p className="text-gray-400 text-xs sm:text-sm text-center sm:text-left">
                © 2025 J&#39;s Landscaping Services LLC. All rights reserved.
              </p>
              <a 
                href="https://readdy.ai/?ref=logo" 
                className="text-gray-400 hover:text-[#83B328] transition-colors text-xs sm:text-sm cursor-pointer"
                target="_blank"
                rel="nofollow noopener noreferrer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Booking Choice Modal */}
      {showBookingChoice && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden animate-[fadeIn_0.2s_ease-out]">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#24820C] to-[#83B328] p-5 sm:p-6 text-center relative">
              <button
                onClick={closeModal}
                className="absolute top-3 right-3 sm:top-4 sm:right-4 text-white/80 hover:text-white cursor-pointer transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                {bookingStep === 'choice' && 'How Would You Like to Book?'}
                {bookingStep === 'zip' && 'Check Your Service Area'}
                {bookingStep === 'result' && (zipValid ? 'You\'re In Our Area!' : 'Outside Service Area')}
              </h2>
              <p className="text-white/90 text-sm">
                {bookingStep === 'choice' && 'Choose the option that works best for you'}
                {bookingStep === 'zip' && 'Enter your zip code to verify we serve your area'}
                {bookingStep === 'result' && (zipValid ? 'Great news — we can schedule your evaluation' : 'Unfortunately we don\'t serve that area yet')}
              </p>
            </div>

            <div className="p-5 sm:p-6">
              {/* Step 1: Choice */}
              {bookingStep === 'choice' && (
                <div className="space-y-4">
                  {/* Instant Service */}
                  <a
                    href="https://book.jslandscapingmd.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closeModal}
                    className="group relative block bg-[#83B328]/5 hover:bg-[#83B328]/10 border-2 border-[#83B328] hover:border-[#6d9620] rounded-xl p-5 transition-all cursor-pointer ring-2 ring-[#83B328]/20"
                  >
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-[#83B328] text-white text-xs font-bold px-4 py-1 rounded-full shadow-lg whitespace-nowrap">
                        ⭐ MOST POPULAR
                      </span>
                    </div>
                    <div className="flex items-center mt-1">
                      <div className="w-14 h-14 bg-[#83B328]/15 rounded-full flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#83B328]/25 transition-colors">
                        <i className="ri-flashlight-line text-2xl text-[#83B328]"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Book Instant Service</h3>
                        <p className="text-sm text-gray-600">Get an instant quote and book right away — no waiting</p>
                        <div className="flex items-center text-sm text-[#83B328] font-semibold mt-1">
                          <i className="ri-lightning-line mr-1"></i>
                          <span>Instant booking available</span>
                        </div>
                      </div>
                    </div>
                  </a>

                  {/* In-Person Evaluation */}
                  <button
                    onClick={handleInPerson}
                    className="group w-full text-left bg-gray-50 hover:bg-gray-100 border-2 border-gray-200 hover:border-[#24820C] rounded-xl p-5 transition-all cursor-pointer"
                  >
                    <div className="flex items-center">
                      <div className="w-14 h-14 bg-[#24820C]/10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 group-hover:bg-[#24820C]/20 transition-colors">
                        <i className="ri-user-location-line text-2xl text-[#24820C]"></i>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 mb-1">Book In-Person Evaluation</h3>
                        <p className="text-sm text-gray-600">Schedule a free on-site consultation with our expert team</p>
                        <div className="flex items-center text-sm text-gray-500 mt-1">
                          <i className="ri-calendar-line mr-1"></i>
                          <span>Takes 1-2 business days</span>
                        </div>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {/* Step 2: Zip Code */}
              {bookingStep === 'zip' && (
                <div>
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Your Zip Code</label>
                    <input
                      type="text"
                      value={zipCode}
                      onChange={(e) => {
                        const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                        setZipCode(val);
                      }}
                      placeholder="e.g. 21014"
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-[#83B328] focus:border-transparent text-lg text-center tracking-widest font-semibold"
                      maxLength={5}
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') handleZipSubmit(); }}
                    />
                  </div>

                  <button
                    onClick={handleZipSubmit}
                    disabled={zipCode.trim().length !== 5}
                    className="w-full bg-[#24820C] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#1d6a0a] transition-all disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer text-base shadow-lg"
                  >
                    Check Availability
                  </button>

                  <button
                    onClick={() => setBookingStep('choice')}
                    className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm font-medium cursor-pointer py-2"
                  >
                    <i className="ri-arrow-left-line mr-1"></i> Back
                  </button>
                </div>
              )}

              {/* Step 3: Result */}
              {bookingStep === 'result' && (
                <div className="text-center">
                  {zipValid ? (
                    <>
                      <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-check-line text-3xl text-green-600"></i>
                      </div>
                      <p className="text-gray-700 mb-2 text-base font-medium">
                        Zip code <strong>{zipCode}</strong> is in our service area!
                      </p>
                      <p className="text-gray-500 text-sm mb-6">
                        You qualify for a free in-person property evaluation.
                      </p>
                      <button
                        onClick={handleGoToBooking}
                        className="w-full bg-[#83B328] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#6d9620] transition-all whitespace-nowrap cursor-pointer text-base shadow-lg"
                      >
                        Schedule My Free Evaluation
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <i className="ri-map-pin-line text-3xl text-red-500"></i>
                      </div>
                      <p className="text-gray-700 mb-2 text-base font-medium">
                        Sorry, <strong>{zipCode}</strong> is outside our current service area.
                      </p>
                      <p className="text-gray-500 text-sm mb-6">
                        We currently serve Harford County and surrounding areas in Maryland. You can still book an instant service online!
                      </p>
                      <a
                        href="https://book.jslandscapingmd.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={closeModal}
                        className="inline-block w-full bg-[#83B328] text-white px-6 py-3 rounded-xl font-bold hover:bg-[#6d9620] transition-all whitespace-nowrap cursor-pointer text-base shadow-lg text-center"
                      >
                        Book Instant Service Instead
                      </a>
                    </>
                  )}

                  <button
                    onClick={() => { setBookingStep('choice'); setZipCode(''); setZipValid(null); }}
                    className="w-full mt-3 text-gray-500 hover:text-gray-700 text-sm font-medium cursor-pointer py-2"
                  >
                    <i className="ri-arrow-left-line mr-1"></i> Start Over
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
